import React, { createContext, useContext, useReducer } from "react";
import PropTypes from "prop-types";

// Repeat types
export const REPEAT_TYPES = {
  DAY: "DAILY",
  WEEK: "WEEKLY",
  MONTH: "MONTHLY",
};

// End types
export const END_TYPES = {
  ACADEMIC_YEAR: "academic_year",
  ON_DATE: "on_date",
  AFTER_OCCURRENCES: "after_occurrences",
};

// Action types
const ACTION_TYPES = {
  SET_INTERVAL: "SET_INTERVAL",
  SET_REPEAT_TYPE: "SET_REPEAT_TYPE",
  SET_END_TYPE: "SET_END_TYPE",
  SET_END_DATE: "SET_END_DATE",
  SET_OCCURRENCES: "SET_OCCURRENCES",
  SET_SKIP_NON_OPERATIONAL_DAYS: "SET_SKIP_NON_OPERATIONAL_DAYS",
  RESET_STATE: "RESET_STATE",
  SET_WEEKDAYS: "SET_WEEKDAYS",
  SET_MONTHDAYS: "SET_MONTHDAYS",
  SET_POSITIONS: "SET_POSITIONS",
  SET_INITIAL_STATE: "SET_INITIAL_STATE",
  SET_DAYS: "SET_DAYS",
};

// Initial state
const getInitialState = academicYearEndDate => ({
  interval: 1,
  repeatType: REPEAT_TYPES.DAY,
  endType: END_TYPES.ACADEMIC_YEAR,
  endDate: academicYearEndDate,
  occurrences: 2,
  skipNonOperationalDays: false,
  academicYearEndDate,
  weekDays: [],
  monthDays: [],
  setPositions: [],
});

// Reducer
const repeatEventReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_INTERVAL:
      return {
        ...state,
        interval: Math.max(1, parseInt(action.payload, 10) || 1),
      };
    case ACTION_TYPES.SET_WEEKDAYS:
      return {
        ...state,
        weekDays: action.payload,
        // setPositions: [],
        monthDays: [],
      };

    case ACTION_TYPES.SET_MONTHDAYS:
      return {
        ...state,
        monthDays: action.payload,
        setPositions: [],
        weekDays: [],
      };

    case ACTION_TYPES.SET_POSITIONS:
      return {
        ...state,
        setPositions: action.payload,
        monthDays: [],
        // weekDays: [],
      };

    case ACTION_TYPES.SET_REPEAT_TYPE:
      return {
        ...state,
        repeatType: action.payload,
      };

    case ACTION_TYPES.SET_END_TYPE:
      return {
        ...state,
        endType: action.payload,
      };

    case ACTION_TYPES.SET_DAYS:
      return {
        ...state,
        monthDays: [],
        weekDays: [],
        setPositions: [],
      };

    case ACTION_TYPES.SET_END_DATE:
      return {
        ...state,
        endDate: action.payload,
      };

    case ACTION_TYPES.SET_OCCURRENCES:
      return {
        ...state,
        occurrences: Math.max(1, parseInt(action.payload, 10) || 1),
      };

    case ACTION_TYPES.SET_SKIP_NON_OPERATIONAL_DAYS:
      return {
        ...state,
        skipNonOperationalDays: action.payload,
      };

    case ACTION_TYPES.RESET_STATE:
      return getInitialState(action.payload || state.academicYearEndDate);

    case ACTION_TYPES.SET_INITIAL_STATE:
      return {
        ...state,
        ...action.payload,
      };

    default:
      return state;
  }
};

// Context
const RepeatEventContext = createContext();

// Provider component
export const RepeatEventProvider = ({ children, academicYearEndDate }) => {
  const [state, dispatch] = useReducer(
    repeatEventReducer,
    getInitialState(academicYearEndDate)
  );

  const actions = {
    setInterval: interval => {
      dispatch({ type: ACTION_TYPES.SET_INTERVAL, payload: interval });
    },

    setWeekDays: weekDays => {
      dispatch({ type: ACTION_TYPES.SET_WEEKDAYS, payload: weekDays });
    },

    setMonthDays: monthDays => {
      dispatch({ type: ACTION_TYPES.SET_MONTHDAYS, payload: monthDays });
    },

    setDays: () => {
      dispatch({ type: ACTION_TYPES.SET_DAYS });
    },

    setPositions: positions => {
      dispatch({ type: ACTION_TYPES.SET_POSITIONS, payload: positions });
    },

    setRepeatType: repeatType => {
      dispatch({ type: ACTION_TYPES.SET_REPEAT_TYPE, payload: repeatType });
    },

    setEndType: endType => {
      dispatch({ type: ACTION_TYPES.SET_END_TYPE, payload: endType });
    },

    setEndDate: endDate => {
      dispatch({ type: ACTION_TYPES.SET_END_DATE, payload: endDate });
    },

    setOccurrences: occurrences => {
      dispatch({ type: ACTION_TYPES.SET_OCCURRENCES, payload: occurrences });
    },

    setSkipNonOperationalDays: skip => {
      dispatch({
        type: ACTION_TYPES.SET_SKIP_NON_OPERATIONAL_DAYS,
        payload: skip,
      });
    },

    resetState: newAcademicYearEndDate => {
      dispatch({
        type: ACTION_TYPES.RESET_STATE,
        payload: newAcademicYearEndDate,
      });
    },

    setInitialState: state => {
      dispatch({ type: "SET_INITIAL_STATE", payload: state });
    },
  };

  const value = {
    state,
    actions,
  };

  return (
    <RepeatEventContext.Provider value={value}>
      {children}
    </RepeatEventContext.Provider>
  );
};

RepeatEventProvider.propTypes = {
  children: PropTypes.node.isRequired,
  academicYearEndDate: PropTypes.string.isRequired,
};

// Hook to use the context
export const useRepeatEvent = () => {
  const context = useContext(RepeatEventContext);
  if (!context) {
    throw new Error("useRepeatEvent must be used within a RepeatEventProvider");
  }
  return context;
};

export default RepeatEventContext;
